import React, { createContext, useContext, useState, useCallback, useMemo } from "react";

/* PASO 3 (F3B) — ConfigContext: tasa de cambio + moneda + formatters puros.
 * effectiveUsd NO vive aquí (depende de user.role — queda en App).
 * siteStatus NO vive aquí (mezcla polling público + handlers admin). */
const ConfigContext = createContext(null);

export function ConfigProvider({ children }) {
  const [exchangeRate, setExchangeRateState] = useState(() => {
    try {
      const v = window.localStorage?.getItem("ag7_exchange_rate");
      const n = v == null ? NaN : Number(v);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch (e) { console.warn("exchange_rate cache read failed:", e); return null; }
  });
  // La caché sólo mejora la primera pintura. Nunca habilita una compra hasta
  // que una respuesta actual del servidor confirma la tasa del catálogo.
  const [rateConfirmed, setRateConfirmed] = useState(false);
  const setExchangeRate = useCallback((value) => {
    setExchangeRateState(value);
    setRateConfirmed(true);
    try { window.localStorage?.setItem("ag7_exchange_rate", String(value)); }
    catch (e) { console.warn("exchange_rate cache write failed:", e); }
  }, []);
  const invalidateExchangeRate = useCallback(() => {
    setExchangeRateState(null);
    setRateConfirmed(false);
    try { window.localStorage?.removeItem("ag7_exchange_rate"); }
    catch (e) { console.warn("exchange_rate cache clear failed:", e); }
  }, []);
  // La moneda predeterminada es SIEMPRE Bs: cada sesión arranca en bolívares
  // para que nadie pague en $ por error (el cambio a USD pide confirmación
  // explícita en la cabecera). Por eso NO se persiste la preferencia.
  const [currency, setCurrency] = useState("BS");

  const value = useMemo(() => {
    const rateReady = rateConfirmed && Number.isFinite(exchangeRate) && exchangeRate > 0;
    const usdToBs = (usd) => {
      if (!rateReady) return null;
      return Math.round(Number(usd) * exchangeRate);
    };
    const formatBs = (n) => {
      if (n == null || Number.isNaN(Number(n))) return "Bs —";
      const num = Math.round(Number(n) || 0);
      return `Bs ${num.toLocaleString("es-VE")}`;
    };
    const formatUsd = (n) => {
      const num = Number(n) || 0;
      return `$${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };
    const formatMoney = (usd) => {
      const usdNum = Number(usd) || 0;
      const bsNum = usdToBs(usdNum);
      if (currency === "USD") {
        return { primary: formatUsd(usdNum), secondary: formatBs(bsNum), currency: "USD" };
      }
      return { primary: formatBs(bsNum), secondary: formatUsd(usdNum), currency: "BS" };
    };
    const formatItemMoney = (item) => {
      if (!item) return { primary: "", secondary: "", currency };
      return currency === "USD"
        ? { primary: formatUsd(item.priceUsd), secondary: formatBs(item.priceBs), currency: "USD" }
        : { primary: formatBs(item.priceBs), secondary: formatUsd(item.priceUsd), currency: "BS" };
    };
    return {
      exchangeRate, setExchangeRate, invalidateExchangeRate, currency, setCurrency,
      rateReady, usdToBs, formatBs, formatUsd, formatMoney, formatItemMoney,
    };
  }, [exchangeRate, currency, rateConfirmed, setExchangeRate, invalidateExchangeRate]);

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export function useConfig() {
  const ctx = useContext(ConfigContext);
  if (ctx == null) throw new Error("useConfig must be used inside <ConfigProvider>");
  return ctx;
}
